"use client";

import React, { useState, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash, PiArrowUpRight, PiCamera } from "react-icons/pi";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";
import logger from "@/utils/logger";

const CAMPUS_IMAGES = [
  "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&h=400&fit=crop",
  "https://images.unsplash.com/photo-1562774053-701939374585?w=800&h=400&fit=crop",
  "https://images.unsplash.com/photo-1519452635265-7b1fbfd1e4e0?w=800&h=400&fit=crop",
];

export default function UniversityCampusLife({ content, onContentChange, isSelected }: SectionContentProps) {
  const safeContent = content || {};
  const items = safeContent.items || [
    { title: "Student Life", description: "Building a vibrant community of creative and accomplished people from around.", link: "#" },
    { title: "Arts & Culture", description: "Building a vibrant community of creative and accomplished people from around.", link: "#" },
    { title: "Athletics", description: "Building a vibrant community of creative and accomplished people from around.", link: "#" },
  ];

  // Set the first item hovered by default so it matches the reference mockup look at first glance
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(0);
  const [activeUploadIdx, setActiveUploadIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addItem = () => {
    const newItems = [...items, { title: "New Item", description: "Description", link: "#", imageUrl: "" }];
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const removeItem = (idx: number) => {
    const newItems = items.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const updateItem = (idx: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    onContentChange?.({ ...safeContent, items: newItems });
  };

  const handleImageUpload = async (file: File, idx: number) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const uploadPromise = homepageApi.uploadImage(file);
    toast.promise(uploadPromise, {
      loading: "Uploading background image...",
      success: "Image uploaded successfully!",
      error: "Failed to upload image",
    });

    try {
      const response = await uploadPromise;
      if (response.success && response.data?.url) {
        updateItem(idx, "imageUrl", response.data.url);
      } else {
        toast.error(response.message || "Upload failed");
      }
    } catch (err) {
      logger.error(err);
      toast.error("Upload failed");
    }
  };

  return (
    <section className="py-20 px-6 lg:px-8 bg-white overflow-hidden">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-5 items-start">
          
          {/* Hidden File Input for uploading card images */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && activeUploadIdx !== null) {
                handleImageUpload(file, activeUploadIdx);
              }
            }}
          />

          {/* Left Side: Title & Curve Arrow */}
          <div className="lg:col-span-2">
            <div>
              <h2 className="mb-6 text-4xl lg:text-5xl font-bold font-serif text-[#1A1A1A] uppercase tracking-wide">
                <EditableText
                  value={safeContent.title || "Campus Life"}
                  onChange={(val) => onContentChange?.({ ...safeContent, title: val })}
                  as="span"
                  placeholder="Section Title"
                />
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-md">
                <EditableText
                  value={safeContent.description || "Embark on a journey of knowledge, discovery, and growth at Instacks University. Our admissions process is designed to identify bright, motivated individuals who are eager to contribute to our dynamic academic community."}
                  onChange={(val) => onContentChange?.({ ...safeContent, description: val })}
                  as="span"
                  placeholder="Description"
                />
              </p>
            </div>
          </div>

          {/* Right Side: Expanding Hover Cards */}
          <div className="lg:col-span-3">
            <div className="flex flex-col gap-4">
              {items.map((item: any, idx: number) => {
                const isHovered = hoveredIdx === idx;
                const cardImage = item.imageUrl || CAMPUS_IMAGES[idx % CAMPUS_IMAGES.length];

                return (
                  <div
                    key={idx}
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    className="relative flex items-center justify-between overflow-hidden rounded-xl border border-gray-100/80 transition-all duration-500 ease-in-out cursor-pointer"
                    style={{
                      minHeight: isHovered ? "160px" : "110px",
                      padding: "24px",
                    }}
                  >
                    {/* Absolute Background Image (Visible on Hover Only) */}
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
                      style={{
                        backgroundImage: `url(${cardImage})`,
                        opacity: isHovered ? 1 : 0,
                        transform: isHovered ? "scale(1.05)" : "scale(1.0)",
                      }}
                    />
                    
                    {/* Dark Overlay (Hover only) */}
                    <div
                      className="absolute inset-0 bg-black/45 transition-opacity duration-500"
                      style={{ opacity: isHovered ? 1 : 0 }}
                    />

                    {/* Content Area (Relative to sit above absolute elements) */}
                    <div className="relative z-10 flex-1 pr-6 flex flex-col justify-center">
                      <h4 
                        className="text-2xl font-bold font-serif transition-colors duration-300"
                        style={{ color: isHovered ? "#FFFFFF" : "#1A1A1A" }}
                      >
                        <EditableText
                          value={item.title}
                          onChange={(val) => updateItem(idx, "title", val)}
                          as="span"
                          placeholder="Item Title"
                        />
                      </h4>
                      <p 
                        className="text-xs transition-colors duration-300 mt-2 leading-relaxed max-w-lg"
                        style={{ color: isHovered ? "rgba(255,255,255,0.85)" : "#6c7279" }}
                      >
                        <EditableText
                          value={item.description}
                          onChange={(val) => updateItem(idx, "description", val)}
                          as="span"
                          placeholder="Description"
                        />
                      </p>
                    </div>

                    {/* Arrow Button */}
                    <div className="relative z-10 flex-shrink-0">
                      <a
                        href={item.link || "#"}
                        className="flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-300"
                        style={{
                          borderColor: isHovered ? "rgba(255,255,255,0.4)" : "#E5E7EB",
                          color: isHovered ? "#FFFFFF" : "#4B5563",
                          backgroundColor: "transparent",
                        }}
                      >
                        <PiArrowUpRight className="h-5 w-5" />
                      </a>
                    </div>

                    {/* Image Editing Button (Builder Mode Only) */}
                    {isSelected && (
                      <div className="absolute top-2 left-2 z-20 flex gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveUploadIdx(idx);
                            fileInputRef.current?.click();
                          }}
                          className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors"
                          title="Change Item Image"
                        >
                          <PiCamera className="h-4 w-4" />
                        </button>
                      </div>
                    )}

                    {/* Inline Delete Button (Builder Mode Only) */}
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(idx);
                        }}
                        className="absolute top-2 right-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-md transition-colors"
                      >
                        <PiTrash className="h-3.5 w-3.5" />
                      </button>
                    )}

                  </div>
                );
              })}

              {/* Builder Mode: Add Button */}
              {isSelected && (
                <button
                  onClick={addItem}
                  className="flex h-20 items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-500 hover:bg-gray-100/50 hover:text-gray-600 transition-all mt-2"
                >
                  <PiPlus className="h-6 w-6 mr-2" />
                  <span className="text-sm font-medium">Add Campus Life Item</span>
                </button>
              )}

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
